// @wc-ignore-file

// Data-access layer: every string here is technical (RPC names, thrown
// diagnostics), never UI copy — this module has always had zero catalog
// entries. Without the directive above, wuchale extracts a literal like
// `set_default_ai_persona failed: ${reason}` and rewrites it to a catalog
// lookup, which injects the React runtime into a plain module and makes any
// call from outside a component die with "Cannot read properties of null
// (reading 'useState')". User-facing wording for these failures lives in the
// UI layer, where extraction works — see container-members-dialog-hooks.
import { supabase } from "@/lib/supabase";
import type {
  ActorType,
  Container,
  ContainerMember,
  ContainerMemberInsert,
  ContainerRole,
  ContainerType,
  CreateProjectInput,
  CreateWorkspaceInput,
  Project,
  UserMember,
  Workspace,
  WorkspaceRole,
} from "@shared/types/supabase.types";
import type {
  ActorByType,
  ActorInsertByType,
  AddMemberByType,
  ContainerByType,
  Member,
  MemberActorType,
  MemberByType,
} from "../containers.types";
import { isActorType, isContainerType, isMemberType } from "../containers.utils";

// CONTAINERS

/**
 * Fetch all projects for a given user under a specific parent container.
 *
 * This uses the `container_members` table to ensure the user is a member of the
 * projects being returned, and filters projects by `parent_container_id`.
 */
export async function getProjectsOfUserByParentContainerId(
  userId: string,
  parentContainerId: string,
): Promise<Project[]> {
  const { data, error } = await supabase
    .from("container_members")
    .select(
      `
        id,
        role,
        containers!container_members_container_id_fkey!inner (*),
        actors!container_members_actor_id_fkey!inner ( id, actor_type, user_id )
      `,
    )
    .eq("actors.user_id", userId)
    .eq("containers.container_type", "project")
    .eq("containers.parent_container_id", parentContainerId);

  if (error) {
    throw new Error(
      `Failed to fetch projects of user ${userId} under parent container ${parentContainerId}: ${error.message}`,
    );
  }

  const projects = data
    .map((row) => row.containers)
    .filter((container): container is Container => isContainerType("project")(container))
    .map((project) => {
      if (!project.slug) {
        throw new Error(`Project ${project.id} is missing a slug. Projects must have a slug set.`);
      }
      return { ...project, slug: project.slug } as Project;
    });

  return projects;
}

/**
 * Fetch workspace by slug.
 *
 * Note: Queries containers table filtered by workspace type.
 * Workspaces are guaranteed to have non-null slugs.
 */
export async function getWorkspaceBySlug(slug: string): Promise<Workspace> {
  const { data, error } = await supabase.from("workspaces").select("*").eq("slug", slug).single();

  if (error) {
    throw new Error(`Failed to fetch workspace by slug "${slug}": ${error.message}`);
  }

  return data;
}

/**
 * CR-183: the caller's standing on a workspace — `owner` / `admin` / `member`,
 * `contributor` when they only hold the contributor anchor row, or null.
 */
export async function getMyWorkspaceAccess(workspaceId: string): Promise<ContainerRole | null> {
  const { data, error } = await supabase.rpc("my_workspace_access", {
    p_workspace_id: workspaceId,
  });

  if (error) {
    throw new Error(`Failed to read workspace access for ${workspaceId}: ${error.message}`);
  }

  return data ?? null;
}

/**
 * Fetch project by slug.
 *
 * Note: Projects are guaranteed to have non-null slugs.
 */
export async function getProjectBySlugs(
  workspaceSlug: string,
  projectSlug: string,
): Promise<Project> {
  const parentWorkspace = await getWorkspaceBySlug(workspaceSlug);
  const parentWorkspaceId = parentWorkspace.id;

  const { data, error } = await supabase
    .from("projects")
    .select("*")
    .eq("parent_container_id", parentWorkspaceId)
    .eq("slug", projectSlug)
    .single();

  if (error) {
    throw new Error(
      `Failed to fetch workspace by workspace slug "${workspaceSlug}" and project slug "${projectSlug}": ${error.message}`,
    );
  }

  return data;
}

/**
 * Fetch all containers for the current authenticated user by container type
 * Returns Workspace[] when containerType is 'workspace', and Project[] when containerType is 'project'
 */
export async function getContainersOfUserAndContainerType<T extends ContainerType>(
  userId: string,
  containerType: T,
): Promise<ContainerByType<T>[]> {
  const { data, error } = await supabase
    .from("container_members")
    .select(
      `
      id,
      role,
      containers!container_members_container_id_fkey!inner ( * ),
      actors!container_members_actor_id_fkey!inner ( id, actor_type, user_id )
    `,
    )
    .eq("actors.user_id", userId)
    .eq("containers.container_type", containerType);

  if (error) {
    throw new Error(`Failed to fetch ${containerType} of authenticated user: ${error.message}`);
  }

  const containers = data.map((row) => row.containers);

  if (containerType === "project") {
    return containers
      .filter((container): container is Container => isContainerType("project")(container))
      .map((project) => {
        if (!project.slug) {
          throw new Error(
            `Project ${project.id} is missing a slug. Projects must have a slug set.`,
          );
        }
        return { ...project, slug: project.slug } as Project;
      }) as ContainerByType<T>[];
  }

  if (containerType === "workspace") {
    return containers
      .filter((container): container is Container => isContainerType("workspace")(container))
      .map((workspace) => {
        if (!workspace.slug) {
          throw new Error(
            `Workspace ${workspace.id} is missing a slug. Workspaces must have a slug set.`,
          );
        }
        return { ...workspace, slug: workspace.slug } as Workspace;
      }) as ContainerByType<T>[];
  }

  return containers
    .filter((container): container is Container => isContainerType(containerType)(container))
    .map((container) => container as ContainerByType<T>);
}

/**
 * Fetch container by ID
 */
export async function getContainerByContainerId(containerId: string): Promise<Container> {
  const { data, error } = await supabase
    .from("containers")
    .select("*")
    .eq("id", containerId)
    .single();

  if (error) {
    throw new Error(`Failed to fetch container by container ID "${containerId}": ${error.message}`);
  }

  return data as Container;
}

/**
 * Create a project via the create_project RPC.
 * Returns the created containers row.
 */
export async function createProject(input: CreateProjectInput): Promise<Project> {
  const { data, error } = await supabase.rpc("create_project", input);

  if (error) {
    // Will be displayed in UI layer
    console.log(`Failed to create workspace: ${error.message}`);
    if (error.code === "23505") {
      throw new Error("This project slug is already being used. Try something different.");
    } else {
      throw new Error("Failed to create project.");
    }
  }

  return data as Project;
}

/**
 * Create a workspace via the create_workspace RPC.
 * Returns the created containers row.
 */
export async function createWorkspace(input: CreateWorkspaceInput): Promise<Workspace> {
  const { data, error } = await supabase.rpc("create_workspace", input);

  if (error) {
    // Will be displayed in UI layer
    console.log(`Failed to create workspace: ${error.message}`);
    if (error.code === "23505") {
      throw new Error("This workspace slug is already being used. Try something different.");
    } else if (error.code === "42501") {
      // CR-208: the permission is single-use (consumed by the first creation).
      throw new Error(
        "You cannot create another workspace: a sign-up invitation allows creating one workspace only. Ask the Kairo team to grant you a new one.",
      );
    } else {
      throw new Error("Failed to create workspace.");
    }
  }

  return data as Workspace;
}

/**
 * The database refused a container update for lack of permission (Postgres error code
 * 42501): an RLS WITH CHECK, or a guard trigger such as CR-205's
 * `guard_container_sharing_client_write` (only the owner may change
 * `is_shared` / `is_ai_shared`). The message stays the generic one so callers
 * that show `error.message` are unchanged; a caller that can explain the
 * refusal matches the class and picks its own catalogued copy (this module is
 * outside wuchale's extraction).
 */
export class ContainerUpdateForbiddenError extends Error {
  constructor(cause?: unknown) {
    super("Failed to save changes.");
    this.name = "ContainerUpdateForbiddenError";
    this.cause = cause;
  }
}

/**
 * Update a container with partial fields
 */
export async function updateContainer({
  containerId,
  updates,
}: {
  containerId: string;
  updates: Partial<Container>;
}): Promise<Container> {
  if (!Object.keys(updates).length) {
    throw new Error("No container updates provided");
  }

  const { data, error } = await supabase
    .from("containers")
    .update(updates)
    .eq("id", containerId)
    .select()
    .single();

  if (error) {
    console.log(error.message);
    if (error.code === "23505") {
      throw new Error("This slug is already being used. Try something different.");
    } else if (error.code === "42501") {
      throw new ContainerUpdateForbiddenError(error);
    } else {
      throw new Error(`Failed to save changes.`);
    }
  }
  return data;
}

/**
 * Why `set_default_ai_persona` failed, as a stable machine-readable reason.
 *
 * The API layer is NOT extracted by wuchale (this module has zero catalog
 * references), so anything user-facing thrown from here would ship untranslated.
 * Callers map these reasons to catalogued copy in the UI layer instead, and
 * match on `reason` rather than on message text.
 */
export type SetDefaultAiPersonaFailure =
  /** Chat missing, deleted, or not accessible — the RPC answers all three alike. */
  | "not_accessible"
  /** Bad arguments, e.g. the container is not an AI chat. */
  | "invalid_request"
  | "unknown";

export class SetDefaultAiPersonaError extends Error {
  readonly reason: SetDefaultAiPersonaFailure;

  constructor(reason: SetDefaultAiPersonaFailure, cause?: unknown) {
    super(`set_default_ai_persona failed: ${reason}`);
    this.name = "SetDefaultAiPersonaError";
    this.reason = reason;
    this.cause = cause;
  }
}

/**
 * Set (or clear, with `aiPersonaId: null`) an AI chat's default AI persona —
 * the persona that answers an un-mentioned message (CR-46).
 *
 * Goes through the `set_default_ai_persona` RPC rather than an UPDATE on
 * `containers`: RLS only lets owners/admins update a room, so a plain member's
 * direct write matches 0 rows and fails *silently*. The SECURITY DEFINER RPC
 * writes exactly this one column and re-derives authorization from
 * `can_access_container` — the same population that can post in the chat.
 *
 * Returns the full updated row so callers can seed the container query cache
 * instead of refetching.
 */
export async function setDefaultAiPersona({
  containerId,
  aiPersonaId,
}: {
  containerId: string;
  aiPersonaId: string | null;
}): Promise<Container> {
  const { data, error } = await supabase.rpc("set_default_ai_persona", {
    p_container_id: containerId,
    p_ai_persona_id: aiPersonaId,
  });

  if (error) {
    // 42501 covers "no such chat" and "chat you cannot see" identically — the
    // RPC refuses to be an existence oracle, so neither may the reason.
    const reason: SetDefaultAiPersonaFailure =
      error.code === "42501"
        ? "not_accessible"
        : error.code === "22023"
          ? "invalid_request"
          : "unknown";
    throw new SetDefaultAiPersonaError(reason, error);
  }

  return data as Container;
}

/**
 * Delete a container by ID
 */
export async function deleteContainer(containerId: string) {
  const { error } = await supabase.from("containers").delete().eq("id", containerId);

  if (error) {
    console.log(`Failed to delete container: ${error.message}`);
    throw new Error(`Failed to delete.`);
  }

  return true;
}

// CONTAINER MEMBERS

/**
 * Fetch container members for a given container and actor type.
 */
export async function getContainerMembers<T extends MemberActorType>(
  containerId: string,
  actorTypes: readonly T[],
  search?: string,
  orderBy?: "created_at" | "updated_at",
  sortBy?: "asc" | "desc",
): Promise<MemberByType<T>[]> {
  if (!actorTypes.length) {
    throw new Error("At least one actor type must be provided.");
  }

  const searchTerm = search?.trim() || null;

  const { data, error } = await supabase.rpc("get_container_members_by_actor_types", {
    p_container_id: containerId,
    p_actor_types: [...actorTypes] as MemberActorType[],
    p_search: searchTerm ?? undefined,
    p_order_by: orderBy ?? undefined,
    p_sort_by: sortBy ?? undefined,
  });

  if (error) {
    throw new Error(
      `Failed to fetch container members for container ${containerId}: ${error.message}`,
    );
  }

  const members = data.map((row) => {
    if (row.actor_type === "user") {
      return {
        container_member_id: row.container_member_id,
        container_id: row.container_id,
        actor_id: row.actor_id,
        role: row.role,
        job_title: row.job_title,
        container_member_created_at: row.container_member_created_at,
        container_member_updated_at: row.container_member_updated_at,
        actor_type: row.actor_type,
        user_id: row.user_id,
        profile_email: row.profile_email,
        profile_first_name: row.profile_first_name,
        profile_last_name: row.profile_last_name,
        profile_avatar_url: row.profile_avatar_url,
        profile_updated_at: row.profile_updated_at,
      };
    } else if (row.actor_type === "invited_user") {
      return {
        container_member_id: row.container_member_id,
        container_id: row.container_id,
        actor_id: row.actor_id,
        role: row.role,
        job_title: row.job_title,
        container_member_created_at: row.container_member_created_at,
        container_member_updated_at: row.container_member_updated_at,
        actor_type: row.actor_type,
        invite_id: row.invite_id,
        invite_email: row.invite_email,
        invite_domain: row.invite_domain,
        invite_code: row.invite_code,
      };
    } else if (row.actor_type === "ai") {
      return {
        container_member_id: row.container_member_id,
        container_id: row.container_id,
        actor_id: row.actor_id,
        role: row.role,
        job_title: row.job_title,
        container_member_created_at: row.container_member_created_at,
        container_member_updated_at: row.container_member_updated_at,
        actor_type: row.actor_type,
        ai_persona_id: row.ai_persona_id,
        ai_persona_display_name: row.ai_persona_display_name,
        ai_persona_system_prompt: row.ai_persona_system_prompt,
        ai_persona_description: row.ai_persona_description,
        ai_persona_model: row.ai_persona_model,
        ai_persona_handle: row.ai_persona_handle,
        ai_persona_created_at: row.ai_persona_created_at,
        ai_persona_updated_at: row.ai_persona_updated_at,
        ai_persona_avatar_url: row.ai_persona_avatar_url,
      };
    }

    // CR-111: this used to be a bare `else` that built an AI member out of
    // whatever was left. `actor_type` now has a fourth value, `agent`, and
    // the RPC could grow an agent arm in stage 4 — at which point an agent
    // would have rendered as an AI persona with every field null, silently.
    // The never-assignment makes that a compile error instead.
    const unsupported: never = row.actor_type;
    throw new Error(`Unsupported actor type: ${String(unsupported)}`);
  });

  return members as MemberByType<T>[];
}

/**
 * Update container member email by member ID
 */
export async function updateUserMemberRole(
  containerMemberId: string,
  role: WorkspaceRole,
): Promise<ContainerMember> {
  const { data, error } = await supabase
    .from("container_members")
    .update({ role: role })
    .eq("id", containerMemberId)
    .select()
    .single();

  if (error) {
    console.log(
      `Failed to update container membership for container member ${containerMemberId}: ${error.message}`,
    );
    throw new Error(`Failed to update role.`);
  }

  return getContainerMemberById(data.id);
}

/**
 * Remove container member by member ID
 */
export async function removeMember(containerMemberId: string) {
  const member = await getContainerMemberById(containerMemberId);
  const containerIdsToDeleteFrom = [member.container_id];
  let parentIdsToVisit = [member.container_id];

  while (parentIdsToVisit.length > 0) {
    const { data: children, error: childrenError } = await supabase
      .from("containers")
      .select("id")
      .in("parent_container_id", parentIdsToVisit);

    if (childrenError) {
      console.log(
        `Failed to fetch child containers for container ${member.container_id}: ${childrenError.message}`,
      );
      throw new Error(`Failed to remove member.`);
    }

    const childIds = children.map((child) => child.id);

    if (childIds.length === 0) {
      break;
    }

    containerIdsToDeleteFrom.push(...childIds);
    parentIdsToVisit = childIds;
  }

  const { error } = await supabase
    .from("container_members")
    .delete()
    .eq("actor_id", member.actor_id)
    .in("container_id", containerIdsToDeleteFrom);

  if (error) {
    console.log(
      `Failed to remove container membership for container member ${containerMemberId}: ${error.message}`,
    );
    throw new Error(`Failed to remove member.`);
  }

  return true;
}

/**
 * Fetch (or auto-create for shared containers) the caller's container_members row.
 * Uses ensure_container_member RPC which handles shared-access users who have no
 * explicit row by inserting one on first access.
 */
export async function getContainerMemberByContainerId(
  userId: string,
  containerId: string,
): Promise<ContainerMember> {
  const { data, error } = await supabase.rpc("ensure_container_member", {
    p_container_id: containerId,
  });

  if (error) {
    throw new Error(
      `Failed to fetch container member for user ${userId} by container ID ${containerId}: ${error.message}`,
    );
  }

  return data[0];
}

/**
 * Fetch container member by member ID
 */
export async function getContainerMemberById(memberId: string): Promise<ContainerMember> {
  const { data, error } = await supabase
    .from("container_members")
    .select("*")
    .eq("id", memberId)
    .single();

  if (error) {
    throw new Error(
      `Failed to fetch container member for container member ID ${memberId}: ${error.message}`,
    );
  }

  return data;
}

/**
 * Add a container member row.
 */
export async function upsertContainerMember(
  input: ContainerMemberInsert,
): Promise<ContainerMember> {
  const { data, error } = await supabase
    .from("container_members")
    .upsert(input, {
      onConflict: "actor_id,container_id",
    })
    .select()
    .single();

  if (error) {
    console.log(`Failed to add container member: ${error.message}`);
    throw new Error(`Failed to add member.`);
  }

  return data;
}

// ACTORS

/**
 * Add an actor row.
 */
export async function insertActor<T extends ActorType>(
  input: ActorInsertByType<T>,
): Promise<ActorByType<T>> {
  const { data, error } = await supabase.from("actors").insert(input).select().single();

  if (error) {
    throw new Error(`Failed to create actor: ${error.message}`);
  }

  if (!isActorType(input.actor_type)(data)) {
    throw new Error(`Actor row is inconsistent: expected ${input.actor_type}.`);
  }

  return data;
}

export async function addMember<T extends MemberActorType>(
  input: AddMemberByType<T>,
): Promise<MemberByType<T>> {
  if ("p_user_id" in input) {
    const { data, error } = await supabase.rpc("add_user_member", input);

    if (error) {
      console.log(`Error adding member: ${error.message}`);
      throw new Error(`Error adding member.`);
    }

    if (!isMemberType("user")(data as UserMember | null)) {
      throw new Error("RPC add_user_member returned unexpected member type.");
    }

    return data as MemberByType<T>;
  }

  if ("p_ai_persona_id" in input) {
    const { data, error } = await supabase.rpc("add_ai_member", input);

    if (error) {
      console.log(`Error adding AI member: ${error.message}`);
      throw new Error(`Error adding AI member.`);
    }

    if (!isMemberType("ai")(data as Member | null)) {
      throw new Error("RPC add_ai_member returned unexpected member type.");
    }

    return data as MemberByType<T>;
  }

  if ("p_invite_id" in input) {
    const { data, error } = await supabase.rpc("add_invited_member", input);

    if (error) {
      console.log(`Error adding invited member: ${error.message}`);
      throw new Error(`Error adding member.`);
    }

    if (!isMemberType("invited_user")(data as Member | null)) {
      throw new Error("RPC add_invited_member returned unexpected member type.");
    }
    return data as MemberByType<T>;
  }

  throw new Error("Unsupported addMember input payload.");
}
